import { useEffect, useState, type ReactNode } from "react";

/** Catppuccin Macchiato palette — shared by welcome page and pre-auth landing */
export const C = {
  mantle: "#1e2030",
  surface0: "#363a4f",
  overlay0: "#6e738d",
  text: "#cad3f5",
  subtext: "#a5adcb",
  mauve: "#c6a0f6",
  teal: "#8bd5ca",
  peach: "#f5a97f",
  blue: "#8aadf4",
  green: "#a6da95",
};

export function StageSection({
  number,
  subtitle,
  title,
  description,
  color,
  isLast,
  children,
  palette = C,
}: {
  number: number;
  subtitle: string;
  title: string;
  description: string;
  color: string;
  isLast?: boolean;
  children: (visible: boolean) => ReactNode;
  /** Override palette (defaults to Catppuccin C; pre-auth landing passes a brand palette). */
  palette?: typeof C;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300 + number * 200);
    return () => clearTimeout(timer);
  }, [number]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 32,
        alignItems: "center",
        padding: "32px 0",
        borderBottom: isLast ? "none" : `1px solid ${palette.surface0}40`,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: "all 0.6s ease",
      }}
    >
      {/* Text side */}
      <div style={{ order: number % 2 === 0 ? 2 : 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: `${color}20`,
              border: `1.5px solid ${color}50`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
              color,
            }}
          >
            {number}
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color }}>
            {subtitle}
          </div>
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 8px", color: palette.text }}>{title}</h3>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: palette.subtext, margin: 0 }}>{description}</p>
      </div>

      {/* Visual side */}
      <div
        style={{
          order: number % 2 === 0 ? 1 : 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 160,
          borderRadius: 12,
          backgroundColor: `${palette.mantle}80`,
          border: `1px solid ${palette.surface0}60`,
          padding: 16,
          ...(isLast
            ? {
                border: `1px solid ${color}25`,
                backgroundColor: `${color}06`,
                boxShadow: `0 0 30px ${color}08`,
              }
            : {}),
        }}
      >
        {children(visible)}
      </div>
    </div>
  );
}
